import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { useSimpleToast, getErrorMessage } from '../../components/Toast';
import { CheckCircle, XCircle, Clock, Search, FileText, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Screening({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [screeningCandidate, setScreeningCandidate] = useState(null);
  const [screeningData, setScreeningData] = useState({
    status: 'Shortlisted',
    hr_comments: ''
  });

  useEffect(() => {
    fetchJobs();
    fetchPendingCandidates();
  }, [selectedJob]);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/recruitment/jobs?status=Active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchPendingCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { stage: 'Applied' };
      if (selectedJob) params.job_id = selectedJob;
      
      const response = await axios.get(`${API}/api/recruitment/candidates`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const handleScreening = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/recruitment/screening`, {
        candidate_id: screeningCandidate.id,
        status: screeningData.status,
        hr_comments: screeningData.hr_comments,
        screened_by: user.id || user.employee_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(`Candidate ${screeningData.status.toLowerCase()} successfully!`, 'success');
      setScreeningCandidate(null);
      setScreeningData({ status: 'Shortlisted', hr_comments: '' });
      fetchPendingCandidates();
    } catch (error) {
      console.error('Screening error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Resume Screening</h1>
            <p className="text-gray-600 mt-2">Review and shortlist candidates</p>
          </div>
        </div>

        {/* Stats Cards - Clean white design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#fff3e0'}}>
                <FileText className="w-6 h-6" style={{color: '#ff9800'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#ff9800'}}>{candidates.length}</p>
                <p className="text-sm text-gray-500">Pending Review</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8eaf6'}}>
                <Target className="w-6 h-6" style={{color: '#2b3380'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{Math.min(candidates.length, 10)}</p>
                <p className="text-sm text-gray-500">Today's Target</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e0f7fa'}}>
                <Clock className="w-6 h-6" style={{color: '#00bcd4'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#00bcd4'}}>5 min</p>
                <p className="text-sm text-gray-500">Avg Time</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8f5e9'}}>
                <CheckCircle className="w-6 h-6" style={{color: '#4caf50'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#4caf50'}}>0</p>
                <p className="text-sm text-gray-500">This Week</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <label className="block text-sm font-medium mb-2">Filter by Job Position</label>
          <select
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            className="w-full md:w-1/3 px-3 py-2 border rounded-lg"
          >
            <option value="">All Jobs</option>
            {jobs.map(job => (
              <option key={job.id} value={job.id}>{job.title}</option>
            ))}
          </select>
        </div>

        {/* Candidate Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {candidates.length === 0 ? (
            <div className="col-span-3 bg-white rounded-lg shadow-md p-12 text-center">
              <span className="text-6xl mb-4 block">✅</span>
              <h3 className="text-xl font-bold mb-2" style={{color: '#2b3380'}}>All Caught Up!</h3>
              <p className="text-gray-600">No candidates pending screening</p>
            </div>
          ) : (
            candidates.map((candidate) => {
              const job = jobs.find(j => j.id === candidate.job_id);
              const candidateName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown';
              return (
                <div key={candidate.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold" style={{backgroundColor: '#2b3380'}}>
                      {candidateName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{candidateName}</h3>
                      <p className="text-sm text-gray-600">{job?.title || candidate.job_applied}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Experience:</span>
                      <span className="font-medium">{candidate.experience_years || candidate.experience || 0} years</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Current CTC:</span>
                      <span className="font-medium">₹{candidate.current_ctc?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Expected CTC:</span>
                      <span className="font-medium">₹{candidate.expected_ctc?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Notice Period:</span>
                      <span className="font-medium">{candidate.notice_period}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">Contact</p>
                    <p className="text-sm">{candidate.email}</p>
                    <p className="text-sm">{candidate.phone || candidate.mobile}</p>
                  </div>

                  {candidate.resume_url && (
                    <a
                      href={candidate.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-4 text-sm text-blue-600 hover:underline"
                    >
                      📄 View Resume →
                    </a>
                  )}

                  <button
                    onClick={() => setScreeningCandidate(candidate)}
                    className="w-full py-2 text-white rounded-lg font-medium"
                    style={{backgroundColor: '#2b3380'}}
                  >
                    <Search className="w-4 h-4 inline mr-2" />
                    Screen Candidate
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Screening Modal */}
        {screeningCandidate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>
                Screen: {screeningCandidate.name}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Position</p>
                    <p className="font-medium">{jobs.find(j => j.id === screeningCandidate.job_id)?.title}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Experience</p>
                    <p className="font-medium">{screeningCandidate.experience_years} years</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Current CTC</p>
                    <p className="font-medium">₹{screeningCandidate.current_ctc?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Expected CTC</p>
                    <p className="font-medium">₹{screeningCandidate.expected_ctc?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleScreening}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Screening Decision *</label>
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => setScreeningData({...screeningData, status: 'Shortlisted'})}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        screeningData.status === 'Shortlisted'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${
                        screeningData.status === 'Shortlisted' ? 'text-green-500' : 'text-gray-400'
                      }`} />
                      <p className="text-sm font-medium">Shortlist</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setScreeningData({...screeningData, status: 'Rejected'})}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        screeningData.status === 'Rejected'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300 hover:border-red-300'
                      }`}
                    >
                      <XCircle className={`w-8 h-8 mx-auto mb-2 ${
                        screeningData.status === 'Rejected' ? 'text-red-500' : 'text-gray-400'
                      }`} />
                      <p className="text-sm font-medium">Reject</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setScreeningData({...screeningData, status: 'On Hold'})}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        screeningData.status === 'On Hold'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-300 hover:border-orange-300'
                      }`}
                    >
                      <Clock className={`w-8 h-8 mx-auto mb-2 ${
                        screeningData.status === 'On Hold' ? 'text-orange-500' : 'text-gray-400'
                      }`} />
                      <p className="text-sm font-medium">Hold</p>
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">HR Comments *</label>
                  <textarea
                    value={screeningData.hr_comments}
                    onChange={(e) => setScreeningData({...screeningData, hr_comments: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Add your screening notes, reasons for decision, key observations..."
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-6 py-2 text-white rounded-lg"
                    style={{backgroundColor: '#2b3380'}}
                  >
                    Submit Screening
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setScreeningCandidate(null);
                      setScreeningData({ status: 'Shortlisted', hr_comments: '' });
                    }}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Screening;